import { create } from 'zustand'
import { persist } from 'zustand/middleware'

/** グリッドで占めるマス数。スマホは 4 列なので上限も 4 */
export type GridSpan = 1 | 2 | 3 | 4

export const GRID_SPANS: readonly GridSpan[] = [1, 2, 3, 4]

/** 行×列をそのまま名前にする。行高と列幅は同じなので 1x1 はアイコン 1 つ分 */
export type WidgetSize = `${GridSpan}x${GridSpan}`

export const toWidgetSize = (rows: GridSpan, cols: GridSpan): WidgetSize => `${rows}x${cols}`

const WIDGET_SIZE_SET = new Set<string>(
  GRID_SPANS.flatMap((rows) => GRID_SPANS.map((cols) => toWidgetSize(rows, cols))),
)

export const isWidgetSize = (value: string): value is WidgetSize => WIDGET_SIZE_SET.has(value)

export const parseWidgetSize = (size: WidgetSize): { rows: GridSpan; cols: GridSpan } => {
  const [rows, cols] = size.split('x').map(Number) as [GridSpan, GridSpan]
  return { rows, cols }
}

/** localStorage に入れるため Map ではなく配列で持つ */
type SizeEntry = { key: string; size: WidgetSize }

type HomeLayoutState = {
  /** 並び順。ここに無いキーは既定の順で末尾に付く */
  order: string[]
  sizes: SizeEntry[]
  /** 非表示にしたキー */
  hidden: string[]
  /** 編集モード。永続化しない */
  isEditing: boolean
  setOrder: (order: string[]) => void
  setSize: (key: string, size: WidgetSize) => void
  /** 指定したキーの保存値を捨てる */
  dropSizes: (keys: string[]) => void
  hide: (key: string) => void
  show: (key: string) => void
  setEditing: (isEditing: boolean) => void
  reset: () => void
}

export const useHomeLayoutStore = create<HomeLayoutState>()(
  persist(
    (set) => ({
      order: [],
      sizes: [],
      hidden: [],
      isEditing: false,

      setOrder: (order) => set({ order }),

      setSize: (key, size) =>
        set((state) => ({
          sizes: state.sizes.some((entry) => entry.key === key)
            ? state.sizes.map((entry) => (entry.key === key ? { key, size } : entry))
            : [...state.sizes, { key, size }],
        })),

      dropSizes: (keys) =>
        set((state) => {
          const drop = new Set(keys)
          const sizes = state.sizes.filter((entry) => !drop.has(entry.key))
          return sizes.length === state.sizes.length ? state : { sizes }
        }),

      hide: (key) =>
        set((state) => ({
          hidden: state.hidden.includes(key) ? state.hidden : [...state.hidden, key],
          order: state.order.filter((item) => item !== key),
        })),

      show: (key) =>
        set((state) => ({
          hidden: state.hidden.filter((item) => item !== key),
          order: state.order.includes(key) ? state.order : [...state.order, key],
        })),

      setEditing: (isEditing) => set({ isEditing }),

      reset: () => set({ order: [], sizes: [], hidden: [] }),
    }),
    {
      name: 'home-layout',
      partialize: ({ order, sizes, hidden }) => ({ order, sizes, hidden }),
    },
  ),
)
